import React, { useCallback } from "react";
import { useDialogContext } from "@WahooFitness/wahoo-offline-mfe";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  ListItem,
  ListItemButton,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import { ChevronDownIcon } from "@WahooFitness/redesignr";
import { ConnectedAppListItem } from "./useThirdPartySharing";
import useRecoverySourceToggle from "./useRecoverySourceToggle";
import RecoveryDeviceList from "./RecoveryDeviceList";
import { Trans, t } from "@lingui/macro";

// Capability-filtered flow only: the switch is a mutually-exclusive selector across the
// authorized apps, not an independent on/off toggle. Same card shape as AuthorizedApp, cut
// down to the one row this flow is about.
const RecoverySourceApp: React.FC<ConnectedAppListItem & { currentSourceName?: string }> = ({
  currentSourceName,
  ...app
}) => {
  const {
    icon,
    content,
    authorized,
    handleDeauth,
    nativeHealth,
    nativeHealthRecoveryDataReadable,
  } = app;
  const { setDialog, handleClose } = useDialogContext();
  const { isActive, switchDisabled, handleToggle, releaseIfActive } = useRecoverySourceToggle(
    app,
    currentSourceName
  );

  const handleConfirmDeauth = useCallback(async () => {
    if (await handleDeauth()) {
      await releaseIfActive();
    }
    handleClose();
  }, [handleClose, handleDeauth, releaseIfActive]);

  const handleOpenDeauthDialog = useCallback(() => {
    setDialog({
      open: true,
      title: t`Deauthorize ${content}?`,
      body: t`Are you sure? Deauthorizing will stop sharing across all Wahoo products.`,
      actions: [
        { text: t`Cancel` },
        { text: t`Deauthorize`, color: "error", action: handleConfirmDeauth },
      ],
    });
  }, [content, handleConfirmDeauth, setDialog]);

  if (!authorized) {
    // Shouldn't happen, but typescript.
    return null;
  }

  return (
    <Accordion
      elevation={4}
      disableGutters
      defaultExpanded
      sx={{ "&:last-of-type": { borderRadius: 2 }, "&:first-of-type": { borderRadius: 2 } }}
    >
      <AccordionSummary expandIcon={<ChevronDownIcon />}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box display="flex">{icon}</Box>
          <Box display="flex" flexDirection="column">
            <Typography variant="prose-base">{content}</Typography>
            <Typography variant="prose-sm" color="success.main">
              <Trans>Authorized</Trans>
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pb: 0 }}>
        <Divider />
        <ListItem dense>
          <ListItemText
            primary={nativeHealth ? t`HRV data source` : t`Recovery data source`}
            primaryTypographyProps={{ variant: "prose-base" }}
          />
          <Switch
            checked={isActive}
            onChange={handleToggle}
            color="info"
            disabled={switchDisabled}
          />
        </ListItem>
        {nativeHealth && isActive && <RecoveryDeviceList />}
        {nativeHealth && isActive && !nativeHealthRecoveryDataReadable && (
          // Hidden once native has read an HRV sample; until then denied and empty are indistinguishable.
          <Box px={2} pb={1.5}>
            <Typography
              variant="prose-sm"
              color="text.secondary"
              data-testid="apple-health-recovery-help"
            >
              <Trans>
                Recovery data syncs from Apple Health automatically. If nothing shows up, check that
                Wahoo has access under Health › Profile › Apps › Wahoo, and that a supported device
                is saving data to Apple Health.
              </Trans>
            </Typography>
          </Box>
        )}
        <Divider />
        <ListItem onClick={handleOpenDeauthDialog} disablePadding>
          <ListItemButton sx={{ height: "46px" }}>
            <Typography variant="prose-base" color="error">
              <Trans>Deauthorize</Trans>
            </Typography>
          </ListItemButton>
        </ListItem>
      </AccordionDetails>
    </Accordion>
  );
};

export default RecoverySourceApp;
